import type { Bar } from '../chart/chartModel';
import {
  C,
  applied,
  deviation,
  float,
  int,
  isIndicatorSource,
  method,
  num,
  price,
  upper,
  type IndicatorDef,
  type Params,
} from './defs';
import { EXTRA_INDICATORS } from './extra';
import {
  APPLIED_PRICES,
  MA_METHODS,
  ema,
  highest,
  lowest,
  movingAverage,
  sma,
  smma,
  trueRange,
  type MaMethod,
  type Series,
} from './math';

export type { IndicatorDef, OutputDef, ParamDef, ParamValue, Params } from './defs';

function maDef(id: string, name: string, m: MaMethod): IndicatorDef {
  return {
    id,
    name,
    group: 'Trend',
    overlay: true,
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'ma', label: name, style: 'line', color: C.amber }],
    sourceable: true,
    compute: (bars, p, source) => ({
      ma: movingAverage(price(bars, p, 'applied', source), num(p, 'period'), m),
    }),
    title: (p) => `${upper(id)} (${p.period})`,
  };
}

const parabolicSar = (bars: Bar[], step: number, max: number): Series => {
  const out: Series = new Array<number | null>(bars.length).fill(null);
  if (bars.length < 2) return out;
  // MT5 starts short: the first SAR is the first high, the extreme point is the second low.
  let long = false;
  let af = step;
  let ep = bars[1]!.l;
  let sar = bars[0]!.h;
  out[1] = sar;
  for (let i = 2; i < bars.length; i++) {
    const b = bars[i]!;
    let next = sar + af * (ep - sar);
    if (long) {
      next = Math.min(next, bars[i - 1]!.l, bars[i - 2]!.l);
      if (b.l < next) {
        long = false; // reversal: the SAR jumps to the extreme of the run that just ended
        next = ep;
        ep = b.l;
        af = step;
      } else if (b.h > ep) {
        ep = b.h;
        af = Math.min(af + step, max);
      }
    } else {
      next = Math.max(next, bars[i - 1]!.h, bars[i - 2]!.h);
      if (b.h > next) {
        long = true;
        next = ep;
        ep = b.h;
        af = step;
      } else if (b.l < ep) {
        ep = b.l;
        af = Math.min(af + step, max);
      }
    }
    sar = next;
    out[i] = sar;
  }
  return out;
};

/** MT5's ADX.mq5, bar for bar (see the comment on the ADX definition). */
export function adxMt5(bars: Bar[], n: number): { adx: Series; plus: Series; minus: Series } {
  const k = 2 / (n + 1);
  const adx: Series = [];
  const plus: Series = [];
  const minus: Series = [];
  let pdi = 0;
  let ndi = 0;
  let a = 0;
  bars.forEach((b, i) => {
    if (i === 0) {
      adx.push(null);
      plus.push(null);
      minus.push(null);
      return;
    }
    const prev = bars[i - 1]!;
    let up = b.h - prev.h;
    let down = prev.l - b.l;
    if (up < 0) up = 0;
    if (down < 0) down = 0;
    if (up > down) down = 0;
    else if (up < down) up = 0;
    else up = down = 0;
    const tr = Math.max(Math.abs(b.h - b.l), Math.abs(b.h - prev.c), Math.abs(b.l - prev.c));
    const pd = tr === 0 ? 0 : (100 * up) / tr;
    const nd = tr === 0 ? 0 : (100 * down) / tr;
    pdi = pd * k + pdi * (1 - k);
    ndi = nd * k + ndi * (1 - k);
    const sum = pdi + ndi;
    const dx = sum === 0 ? 0 : (100 * Math.abs(pdi - ndi)) / sum;
    a = dx * k + a * (1 - k);
    adx.push(a);
    plus.push(pdi);
    minus.push(ndi);
  });
  return { adx, plus, minus };
}

export const INDICATORS: IndicatorDef[] = [
  maDef('sma', 'Simple Moving Average', 'sma'),
  maDef('ema', 'Exponential Moving Average', 'ema'),
  maDef('wma', 'Linear Weighted Moving Average', 'lwma'),
  maDef('smma', 'Smoothed Moving Average', 'smma'),
  {
    id: 'bands',
    name: 'Bollinger Bands',
    group: 'Trend',
    overlay: true,
    params: [
      int('period', 'Period', 20),
      float('deviation', 'Deviations', 2, 0.1, 10, 0.1),
      applied('applied', 'Apply to'),
    ],
    outputs: [
      { key: 'middle', label: 'Middle', style: 'line', color: C.amber },
      { key: 'upper', label: 'Upper', style: 'line', color: C.blue },
      { key: 'lower', label: 'Lower', style: 'line', color: C.blue },
    ],
    sourceable: true,
    compute: (bars, p, source) => {
      const values = price(bars, p, 'applied', source);
      const n = num(p, 'period');
      const middle = sma(values, n);
      const sd = deviation(values, middle, n);
      const k = num(p, 'deviation');
      return {
        middle,
        upper: middle.map((m, i) => (m === null || sd[i] === null ? null : m + k * sd[i]!)),
        lower: middle.map((m, i) => (m === null || sd[i] === null ? null : m - k * sd[i]!)),
      };
    },
    title: (p) => `Bollinger (${p.period}, ${p.deviation})`,
  },
  {
    id: 'envelopes',
    name: 'Envelopes',
    group: 'Trend',
    overlay: true,
    params: [
      int('period', 'Period', 14),
      method('method', 'Method'),
      applied('applied', 'Apply to'),
      float('deviation', 'Deviation %', 0.1, 0.01, 100, 0.01),
    ],
    outputs: [
      { key: 'upper', label: 'Upper', style: 'line', color: C.blue },
      { key: 'lower', label: 'Lower', style: 'line', color: C.pink },
    ],
    sourceable: true,
    compute: (bars, p, source) => {
      const ma = movingAverage(price(bars, p, 'applied', source), num(p, 'period'), p.method as MaMethod);
      const d = num(p, 'deviation') / 100;
      return {
        upper: ma.map((v) => (v === null ? null : v * (1 + d))),
        lower: ma.map((v) => (v === null ? null : v * (1 - d))),
      };
    },
    title: (p) => `Envelopes (${p.period}, ${p.deviation}%)`,
  },
  {
    id: 'sar',
    name: 'Parabolic SAR',
    group: 'Trend',
    overlay: true,
    params: [float('step', 'Step', 0.02, 0.001, 1, 0.001), float('maximum', 'Maximum', 0.2, 0.01, 1, 0.01)],
    outputs: [{ key: 'sar', label: 'SAR', style: 'dots', color: C.green }],
    compute: (bars, p) => ({ sar: parabolicSar(bars, num(p, 'step'), num(p, 'maximum')) }),
    title: (p) => `SAR (${p.step}, ${p.maximum})`,
  },
  {
    id: 'ichimoku',
    name: 'Ichimoku Kinko Hyo',
    group: 'Trend',
    overlay: true,
    params: [
      int('tenkan', 'Tenkan-sen', 9),
      int('kijun', 'Kijun-sen', 26),
      int('senkou', 'Senkou Span B', 52),
    ],
    outputs: [
      { key: 'tenkan', label: 'Tenkan-sen', style: 'line', color: C.red },
      { key: 'kijun', label: 'Kijun-sen', style: 'line', color: C.blue },
      { key: 'spanA', label: 'Senkou Span A', style: 'line', color: C.green, shift: (p) => num(p, 'kijun') },
      { key: 'spanB', label: 'Senkou Span B', style: 'line', color: C.pink, shift: (p) => num(p, 'kijun') },
      { key: 'chikou', label: 'Chikou Span', style: 'line', color: C.violet, shift: (p) => -num(p, 'kijun') },
    ],
    compute: (bars, p) => {
      const highs = bars.map((b) => b.h);
      const lows = bars.map((b) => b.l);
      const mid = (n: number): Series =>
        bars.map((_, i) => (i < n - 1 ? null : (highest(highs, i, n) + lowest(lows, i, n)) / 2));
      const tenkan = mid(num(p, 'tenkan'));
      const kijun = mid(num(p, 'kijun'));
      return {
        tenkan,
        kijun,
        spanA: tenkan.map((t, i) => (t === null || kijun[i] === null ? null : (t + kijun[i]!) / 2)),
        spanB: mid(num(p, 'senkou')),
        chikou: bars.map((b) => b.c),
      };
    },
    title: (p) => `Ichimoku (${p.tenkan}, ${p.kijun}, ${p.senkou})`,
  },
  {
    id: 'rsi',
    name: 'Relative Strength Index',
    group: 'Oscillators',
    overlay: false,
    levels: [30, 70],
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'rsi', label: 'RSI', style: 'line', color: C.violet }],
    sourceable: true,
    compute: (bars, p, source) => {
      const values = price(bars, p, 'applied', source);
      const n = num(p, 'period');
      const diff = (i: number) => {
        const v = values[i];
        const before = values[i - 1];
        return i === 0 || v === null || v === undefined || before === null || before === undefined
          ? null
          : v - before;
      };
      const gains: Series = values.map((_, i) => (diff(i) === null ? null : Math.max(diff(i)!, 0)));
      const losses: Series = values.map((_, i) => (diff(i) === null ? null : Math.max(-diff(i)!, 0)));
      const g = smma(gains, n);
      const l = smma(losses, n);
      return {
        // MT5: 100 when there were only gains, 50 when the price did not move at all.
        rsi: g.map((gv, i) =>
          gv === null || l[i] === null
            ? null
            : l[i] === 0
              ? gv === 0
                ? 50
                : 100
              : 100 - 100 / (1 + gv / l[i]!),
        ),
      };
    },
    title: (p) => `RSI (${p.period})`,
  },
  {
    id: 'macd',
    name: 'MACD',
    group: 'Oscillators',
    overlay: false,
    levels: [0],
    params: [
      int('fast', 'Fast EMA', 12),
      int('slow', 'Slow EMA', 26),
      int('signal', 'Signal SMA', 9),
      applied('applied', 'Apply to'),
    ],
    outputs: [
      { key: 'macd', label: 'MACD', style: 'histogram', color: C.green },
      { key: 'signal', label: 'Signal', style: 'line', color: C.red },
    ],
    compute: (bars, p) => {
      const values = price(bars, p) as number[];
      const fast = ema(values, num(p, 'fast'));
      const slow = ema(values, num(p, 'slow'));
      const macd: Series = fast.map((f, i) => (f === null || slow[i] === null ? null : f - slow[i]!));
      return { macd, signal: sma(macd, num(p, 'signal')) };
    },
    title: (p) => `MACD (${p.fast}, ${p.slow}, ${p.signal})`,
  },
  {
    id: 'stochastic',
    name: 'Stochastic Oscillator',
    group: 'Oscillators',
    overlay: false,
    levels: [20, 80],
    params: [int('k', '%K period', 5), int('d', '%D period', 3), int('slowing', 'Slowing', 3)],
    outputs: [
      { key: 'main', label: '%K', style: 'line', color: C.green },
      { key: 'signal', label: '%D', style: 'line', color: C.red },
    ],
    compute: (bars, p) => {
      const highs = bars.map((b) => b.h);
      const lows = bars.map((b) => b.l);
      const k = num(p, 'k');
      const slowing = num(p, 'slowing');
      const closeLow = bars.map((b, i) => (i < k - 1 ? null : b.c - lowest(lows, i, k)));
      const range = bars.map((_, i) => (i < k - 1 ? null : highest(highs, i, k) - lowest(lows, i, k)));
      const main: Series = bars.map((_, i) => {
        if (i < k - 1 + slowing - 1) return null;
        let a = 0;
        let b = 0;
        for (let j = i - slowing + 1; j <= i; j++) {
          a += closeLow[j]!;
          b += range[j]!;
        }
        return b === 0 ? 100 : (a / b) * 100;
      });
      return { main, signal: sma(main, num(p, 'd')) };
    },
    title: (p) => `Stochastic (${p.k}, ${p.d}, ${p.slowing})`,
  },
  {
    id: 'cci',
    name: 'Commodity Channel Index',
    group: 'Oscillators',
    overlay: false,
    levels: [-100, 100],
    params: [int('period', 'Period', 14), applied('applied', 'Apply to', 'typical')],
    outputs: [{ key: 'cci', label: 'CCI', style: 'line', color: C.cyan }],
    compute: (bars, p) => {
      const values = price(bars, p) as number[];
      const n = num(p, 'period');
      const mean = sma(values, n);
      return {
        cci: values.map((v, i) => {
          const m = mean[i];
          if (m === null || m === undefined) return null;
          let md = 0;
          for (let k = i - n + 1; k <= i; k++) md += Math.abs(values[k]! - m);
          md /= n;
          return md === 0 ? 0 : (v - m) / (0.015 * md);
        }),
      };
    },
    title: (p) => `CCI (${p.period})`,
  },
  {
    id: 'wpr',
    name: "Williams' Percent Range",
    group: 'Oscillators',
    overlay: false,
    levels: [-80, -20],
    params: [int('period', 'Period', 14)],
    outputs: [{ key: 'wpr', label: '%R', style: 'line', color: C.blue }],
    compute: (bars, p) => {
      const highs = bars.map((b) => b.h);
      const lows = bars.map((b) => b.l);
      const n = num(p, 'period');
      const wpr: Series = [];
      bars.forEach((b, i) => {
        if (i < n - 1) return wpr.push(null);
        const hh = highest(highs, i, n);
        const ll = lowest(lows, i, n);
        // MT5 keeps the previous value when the window has no range
        wpr.push(hh === ll ? (wpr[i - 1] ?? 0) : ((hh - b.c) / (hh - ll)) * -100);
      });
      return { wpr };
    },
    title: (p) => `%R (${p.period})`,
  },
  {
    id: 'momentum',
    name: 'Momentum',
    group: 'Oscillators',
    overlay: false,
    levels: [100],
    params: [int('period', 'Period', 14), applied('applied', 'Apply to')],
    outputs: [{ key: 'mom', label: 'Momentum', style: 'line', color: C.amber }],
    sourceable: true,
    compute: (bars, p, source) => {
      const values = price(bars, p, 'applied', source);
      const n = num(p, 'period');
      return {
        mom: values.map((v, i) => {
          const before = values[i - n];
          return i < n || v === null || before === null || before === undefined || before === 0
            ? null
            : (v / before) * 100;
        }),
      };
    },
    title: (p) => `Momentum (${p.period})`,
  },
  {
    id: 'adx',
    name: 'Average Directional Index',
    group: 'Oscillators',
    overlay: false,
    levels: [25],
    params: [int('period', 'Period', 14)],
    outputs: [
      { key: 'adx', label: 'ADX', style: 'line', color: C.grey },
      { key: 'plus', label: '+DI', style: 'line', color: C.green },
      { key: 'minus', label: '-DI', style: 'line', color: C.red },
    ],
    // As MT5's ADX.mq5: each bar's +DM/TR and -DM/TR (in %) are smoothed with an exponential average that starts from 0 on
    // the first bar, and so is the DX that gives the ADX.
    compute: (bars, p) => ({ ...adxMt5(bars, num(p, 'period')) }),
    title: (p) => `ADX (${p.period})`,
  },
  {
    id: 'atr',
    name: 'Average True Range',
    group: 'Volatility',
    overlay: false,
    params: [int('period', 'Period', 14)],
    outputs: [{ key: 'atr', label: 'ATR', style: 'line', color: C.amber }],
    // MT5's ATR is a simple average of the true range.
    compute: (bars, p) => ({
      atr: sma(
        trueRange(bars).map((v, i) => (i === 0 ? null : v)),
        num(p, 'period'),
      ),
    }),
    title: (p) => `ATR (${p.period})`,
  },
  {
    id: 'stddev',
    name: 'Standard Deviation',
    group: 'Volatility',
    overlay: false,
    params: [int('period', 'Period', 20), method('method', 'Method'), applied('applied', 'Apply to')],
    outputs: [{ key: 'sd', label: 'StdDev', style: 'line', color: C.pink }],
    sourceable: true,
    compute: (bars, p, source) => {
      const values = price(bars, p, 'applied', source);
      const n = num(p, 'period');
      return { sd: deviation(values, movingAverage(values, n, p.method as MaMethod), n) };
    },
    title: (p) => `StdDev (${p.period})`,
  },
  {
    id: 'volume',
    name: 'Tick Volume',
    group: 'Volumes',
    overlay: false,
    params: [],
    outputs: [{ key: 'volume', label: 'Volume', style: 'histogram', color: C.grey }],
    compute: (bars) => ({ volume: bars.map((b) => b.tv) }),
    title: () => 'Volume',
  },
];

INDICATORS.push(...EXTRA_INDICATORS);

export const indicatorById = (id: string): IndicatorDef | undefined => INDICATORS.find((i) => i.id === id);

export const defaultParams = (def: IndicatorDef): Params =>
  Object.fromEntries(def.params.map((p) => [p.key, p.default]));

export { APPLIED_PRICES, MA_METHODS };

/** Keeps stored parameters usable: unknown keys dropped, missing ones defaulted, numbers clamped to their range. */
export function normalizeParams(def: IndicatorDef, stored: Params | undefined): Params {
  const out: Params = {};
  for (const p of def.params) {
    const raw = stored?.[p.key];
    if (p.type === 'int' || p.type === 'float') {
      const n = Number(raw);
      const value = Number.isFinite(n) ? n : p.default;
      out[p.key] =
        p.type === 'int'
          ? Math.round(Math.min(p.max, Math.max(p.min, value)))
          : Math.min(p.max, Math.max(p.min, value));
    } else if (p.type === 'method') {
      out[p.key] = (MA_METHODS as readonly string[]).includes(String(raw)) ? String(raw) : p.default;
    } else {
      const ok =
        (APPLIED_PRICES as readonly string[]).includes(String(raw)) ||
        (def.sourceable === true && isIndicatorSource(raw));
      out[p.key] = ok ? String(raw) : p.default;
    }
  }
  return out;
}
